import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const COLORS = ['#0ea5e9','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

const fmtCurrency = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); if (p.length === 3 && p[0].length === 4) return `${p[2]}-${p[1]}-${p[0].slice(-2)}`; return d; };

const SODashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try { const res = await api.get('/so-dashboard'); setData(res.data); }
      catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div></div>;
  if (!data) return <div className="p-6 text-center text-gray-500">No data available</div>;

  const { kpis, monthly_trends, customer_breakdown, occasion_breakdown, brand_breakdown, source_breakdown, recent_sos } = data;

  const statusData = Object.entries(kpis.status_counts || {}).map(([name, value]) => ({ name: name.replace('_',' '), value }));

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="so-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Sales Dashboard</h1>
        <p className="text-gray-600 mt-1">Sales orders analytics and insights</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="so-kpis">
        <Card className="border-l-4 border-l-sky-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Total SOs</p>
          <p className="text-2xl font-bold text-gray-900">{kpis.total_sos}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-blue-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Value Ordered</p>
          <p className="text-xl font-bold text-blue-700">{fmtCurrency(kpis.value_ordered)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Value Dispatched</p>
          <p className="text-xl font-bold text-green-700">{fmtCurrency(kpis.value_dispatched)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Value Pending</p>
          <p className="text-xl font-bold text-amber-700">{fmtCurrency(kpis.value_pending)}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-indigo-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Pieces Ordered</p>
          <p className="text-2xl font-bold text-indigo-700">{kpis.total_pieces_ordered?.toLocaleString()}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-4">
          <p className="text-xs text-gray-500">Pieces Dispatched</p>
          <p className="text-2xl font-bold text-emerald-700">{kpis.total_pieces_dispatched?.toLocaleString()}</p>
        </CardContent></Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <Card><CardHeader><CardTitle className="text-base">Monthly Trends</CardTitle></CardHeader>
          <CardContent>
            {monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => v.toLocaleString()} /><Legend />
                  <Line type="monotone" dataKey="orders" stroke="#0ea5e9" strokeWidth={2} name="Orders" />
                  <Line type="monotone" dataKey="pieces" stroke="#10b981" strokeWidth={2} name="Pieces" />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No trend data yet</p>}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card><CardHeader><CardTitle className="text-base">Status Breakdown</CardTitle></CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                    {statusData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No status data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Breakdown */}
        <Card><CardHeader><CardTitle className="text-base">Top Customers by Value</CardTitle></CardHeader>
          <CardContent>
            {customer_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={customer_breakdown.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} /><Bar dataKey="amount" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No customer data yet</p>}
          </CardContent>
        </Card>

        {/* Occasion Breakdown */}
        <Card><CardHeader><CardTitle className="text-base">Orders by Occasion</CardTitle></CardHeader>
          <CardContent>
            {occasion_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={occasion_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No occasion data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Source Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="source-analysis"><CardHeader><CardTitle className="text-base">Order Source Analysis</CardTitle></CardHeader>
          <CardContent>
            {(source_breakdown || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={source_breakdown} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="amount" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {(source_breakdown || []).map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No source data yet — track order sources in Sales Entry</p>}
          </CardContent>
        </Card>

        <Card data-testid="source-revenue"><CardHeader><CardTitle className="text-base">Revenue by Source</CardTitle></CardHeader>
          <CardContent>
            {(source_breakdown || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={source_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} /><Bar dataKey="amount" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No source data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Recent SOs */}
      <Card><CardHeader><CardTitle className="text-base">Recent Sales Orders</CardTitle></CardHeader>
        <CardContent>
          {recent_sos.length > 0 ? (
            <table className="w-full text-sm" data-testid="recent-sos-table">
              <thead className="bg-gray-50"><tr>
                <th className="p-2 text-left">SO Number</th><th className="p-2 text-left">Customer</th><th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Occasion</th><th className="p-2 text-left">Status</th><th className="p-2 text-right">Items</th><th className="p-2 text-right">Amount</th>
              </tr></thead>
              <tbody>{recent_sos.map((so, i) => (
                <tr key={i} className="border-t hover:bg-gray-50">
                  <td className="p-2 font-mono font-semibold text-sky-700">{so.so_number}</td>
                  <td className="p-2">{so.customer}</td>
                  <td className="p-2 text-gray-500">{fmtDate(so.date)}</td>
                  <td className="p-2 capitalize text-gray-500">{so.occasion?.replace('_',' ')}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${so.status === 'fulfilled' ? 'bg-green-100 text-green-700' : so.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{so.status?.replace('_',' ')}</span></td>
                  <td className="p-2 text-right">{so.items}</td>
                  <td className="p-2 text-right font-medium">{fmtCurrency(so.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="text-center text-gray-400 py-12">No sales orders yet</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default SODashboard;
